import React from 'react';
import { RefreshCw, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ERROR_SEVERITY } from '@/utils/error-categorization';
import { formatTimeOfDay } from '@/utils/time-format';

/**
 * RetryInterface Component
 * Provides user-friendly retry controls and feedback
 * 
 * Requirements: 5.3, 5.1
 */
const RetryInterface = ({
  retryState,
  onRetry,
  onCancel,
  className,
  showProgress = true,
  showHistory = false,
  customRetryText,
  disabled = false
}) => {
  const {
    isRetrying,
    attemptCount,
    lastError,
    canRetry,
    retryHistory,
    hasReachedMaxRetries,
    retryProgress
  } = retryState;

  const getRetryButtonText = () => {
    if (customRetryText) return customRetryText;
    if (isRetrying) return 'Retrying...';
    if (attemptCount === 0) return 'Try Again';
    return `Retry (${attemptCount}/3)`;
  };

  const getStatusIcon = () => {
    if (isRetrying) {
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    }
    if (lastError) {
      const severity = lastError.severity;
      if (severity === ERROR_SEVERITY.CRITICAL) {
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      }
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    }
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  const getStatusText = () => {
    if (isRetrying) return 'Attempting to reconnect...';
    if (hasReachedMaxRetries) return 'Maximum retry attempts reached';
    if (lastError) return 'Operation failed';
    return 'Ready to retry';
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Status Display */}
      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
        {getStatusIcon()}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {getStatusText()}
          </p>
          {attemptCount > 0 && (
            <p className="text-xs text-gray-500">
              Attempt {attemptCount} of 3
            </p>
          )}
        </div>
        {isRetrying && (
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            <span>Please wait...</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {showProgress && attemptCount > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Retry Progress</span>
            <span>{Math.round(retryProgress)}%</span>
          </div>
          <Progress 
            value={retryProgress} 
            className="h-2"
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <Button
          onClick={onRetry}
          disabled={!canRetry || isRetrying || disabled}
          className="flex-1"
          variant={hasReachedMaxRetries ? 'outline' : 'default'}
        >
          {isRetrying ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              {getRetryButtonText()}
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              {getRetryButtonText()}
            </>
          )}
        </Button>
        
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="ghost"
            disabled={isRetrying}
          >
            Cancel
          </Button>
        )}
      </div>

      {/* Retry Guidance */}
      {lastError && !isRetrying && (
        <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
          <p className="font-medium mb-1">What you can try:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Check your internet connection</li>
            <li>Refresh the page if the problem persists</li>
            {hasReachedMaxRetries && (
              <li>Contact support if you continue to experience issues</li>
            )}
          </ul>
        </div>
      )}

      {/* Retry History */}
      {showHistory && retryHistory.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium text-gray-700 mb-2">
            Retry History ({retryHistory.length} attempts)
          </summary>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {retryHistory.map((attempt, index) => (
              <div key={index} className="flex justify-between items-start p-2 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">Attempt {index + 1}</p>
                  <p className="text-gray-600">{attempt.error.userMessage?.message}</p>
                </div>
                <span className="text-gray-500">
                  {formatTimeOfDay(attempt.timestamp)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/**
 * PaymentRetryInterface Component
 * Specialized retry interface for payment operations
 */
export const PaymentRetryInterface = ({ 
  retryState, 
  onRetry, 
  onDifferentCard, 
  onContactSupport 
}) => {
  return (
    <div className="space-y-4">
      <RetryInterface
        retryState={retryState}
        onRetry={onRetry}
        customRetryText="Retry Payment"
        className="max-w-md mx-auto"
      />
      
      {/* Payment-specific options */}
      {retryState.lastError && (
        <div className="flex flex-col space-y-2 max-w-md mx-auto">
          <Button
            onClick={onDifferentCard}
            variant="outline"
            size="sm"
            className="w-full"
          >
            Try Different Card
          </Button>
          <Button
            onClick={onContactSupport}
            variant="ghost"
            size="sm"
            className="w-full text-gray-600"
          >
            Contact Support
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * AccountCreationRetryInterface Component
 * Specialized retry interface for account creation operations
 */
export const AccountCreationRetryInterface = ({ 
  retryState, 
  onRetry, 
  onSkipStep, 
  onContactSupport,
  stepName = 'account creation'
}) => {
  return (
    <div className="space-y-4">
      <RetryInterface
        retryState={retryState}
        onRetry={onRetry}
        customRetryText={`Retry ${stepName}`}
        className="max-w-md mx-auto"
        showProgress={true}
      />
      
      {/* Account creation specific options */}
      {retryState.lastError && (
        <div className="text-center space-y-2">
          {onSkipStep && (
            <Button
              onClick={onSkipStep}
              variant="ghost"
              size="sm"
              className="text-gray-600"
            >
              Skip this step for now
            </Button>
          )}
          <div className="text-xs text-gray-500">
            You can complete this setup later in your account settings
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * NetworkRetryInterface Component
 * Specialized retry interface for network operations
 */
export const NetworkRetryInterface = ({ 
  retryState, 
  onRetry, 
  onRefreshPage,
  operationName = 'operation'
}) => {
  return (
    <div className="space-y-4">
      <RetryInterface
        retryState={retryState}
        onRetry={onRetry}
        customRetryText={`Retry ${operationName}`}
        className="max-w-md mx-auto"
      />
      
      {/* Network-specific options */}
      {retryState.hasReachedMaxRetries && (
        <div className="text-center space-y-2">
          <Button
            onClick={onRefreshPage}
            variant="outline"
            size="sm"
          >
            Refresh Page
          </Button>
          <div className="text-xs text-gray-500">
            This will reload the page and reset the connection
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CompactRetryButton Component
 * Minimal retry button for inline use
 */
export const CompactRetryButton = ({ 
  retryState, 
  onRetry, 
  size = 'sm',
  variant = 'outline'
}) => {
  const { isRetrying, canRetry, attemptCount } = retryState;
  
  return (
    <Button
      onClick={onRetry}
      disabled={!canRetry || isRetrying}
      size={size}
      variant={variant}
      className="flex items-center space-x-1"
    >
      <RefreshCw className={cn(
        'h-3 w-3',
        isRetrying && 'animate-spin'
      )} />
      <span>
        {isRetrying ? 'Retrying...' : 
         attemptCount > 0 ? `Retry (${attemptCount})` : 'Retry'}
      </span>
    </Button>
  );
};

/**
 * RetryStatusBadge Component
 * Shows retry status as a badge
 */
export const RetryStatusBadge = ({ retryState }) => {
  const { isRetrying, attemptCount, hasReachedMaxRetries, lastError } = retryState;
  
  const getBadgeColor = () => {
    if (isRetrying) return 'bg-blue-100 text-blue-800';
    if (hasReachedMaxRetries) return 'bg-red-100 text-red-800';
    if (lastError) return 'bg-orange-100 text-orange-800';
    return 'bg-green-100 text-green-800';
  };
  
  const getBadgeText = () => {
    if (isRetrying) return 'Retrying...';
    if (hasReachedMaxRetries) return 'Max retries reached';
    if (attemptCount > 0) return `${attemptCount} attempts`;
    return 'Ready';
  };
  
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
      getBadgeColor()
    )}>
      {getBadgeText()}
    </span>
  );
};

export default RetryInterface;